import { GoogleOAuthProvider } from '@react-oauth/google'
import { Birthday, KingsBirthday } from 'app/birthday'
import React, { Component } from 'react'
import { ConnectedRouter } from 'connected-react-router'
import { Route, Switch } from 'react-router-dom'
import { history } from './configuration'
import { Home } from 'app/home'
import { Faq } from 'app/faq'
import { StoreLocator } from 'app/store-locator'
import { Timeline } from 'app/timeline'
import { NotFound } from 'app/not-found'
import { Product } from 'app/products'
import { Family } from 'app/family'
import { Custom } from 'app/custom'
import {
    fetchApplicationData,
    fetchAppSettings
} from 'shared/store/application'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import {
    fetchOrder,
    removeProductFromCart,
    resetCart
} from 'shared/store/ecommerce/order'
import { connect } from 'react-redux'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { Meta } from 'app/metadata'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import { ScrollToTop } from 'app/common/scroll-to-top'
import { Catalog } from 'app/catalog'
import { CookiesManagementScreen } from 'app/common/cookies/management/screen'
import { NotificationsPermission } from 'app/common/notifications-permission'
import { EcommerceRoute, ProtectedRoute } from 'app/common/routes'
import { Toast } from 'app/common/toast'
import { Congratulations } from 'app/congratulations'
import { DeliveryOptions } from 'app/delivery-options'
import {
    AdyenOneClickPaymentWrapper,
    CartConfirmation as EcommerceCartConfirmation,
    Catalog as EcommerceCatalog,
    DeliveryChoice as EcommerceDeliveryChoice,
    Discounts as EcommerceDiscounts,
    Menu as EcommerceMenu,
    PaymentWrapper as EcommercePaymentWrapper,
    Product as EcommerceProduct,
    RestaurantChoice as EcommerceRestaurantChoice,
    SigninSignupRoute as EcommerceSigninSignupRoute
} from 'app/ecommerce/common'
import { Kingdom } from 'app/kingdom'
import { ResetPassword } from 'app/reset-password'
import { Restaurant } from 'app/restaurant'
import { Satisfaction } from 'app/satisfaction'
import { Signin } from 'app/signin'
import { Signup } from 'app/signup'
import moment from 'moment'
import { loadReCaptcha } from 'react-recaptcha-v3'
import { fetchAvailableFeatures } from 'shared/store/features'
import type { CookiesConsents } from 'shared/store/user'
import {
    consentCookies,
    getAnalyticsData,
    isAuthTokenExpired
} from 'shared/store/user'
import { getWebsocket, setupWebsocket } from 'shared/store/websocket'
import type { ClickAndCollectPromotion, Order } from 'shared/types'
import { featureCodes, recaptchaSiteKeyV3 } from 'shared/utils'
import {
    KingTableNoDesktopScreen,
    KingTableScanScreen,
    KingTableWelcomeScreen
} from 'app-v2/modules'
import { TuPreferesKingDuel } from './tu-preferes-king/duel/TuPreferesKingDuel'
import { TuPreferesKingDuelWin } from './tu-preferes-king/duel/TuPreferesKingDuelWin'
import { TuPreferesKingHome } from './tu-preferes-king/home/TuPreferesKingHome'
import { FeatureToggledRoute } from './common/feature-toggle/FeatureToggledRoute'
import { TuPreferesKingGoodiesGain } from './tu-preferes-king/goodies-gain/TuPreferesKingGoodiesGain'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MFAConfirmCode } from './signin/mfa'
import { Complaint } from './complaint'
import { OneTrustCookieManagementScreen } from 'app-v2/modules/onetrust/OneTrustCookieManagementScreen'
import { TuPreferesKingQrCode } from './tu-preferes-king/qr-code/TuPreferesKingQrCode'
import { TuPreferesKingEmailForm } from './tu-preferes-king/email-form/TuPreferesKingEmailForm'
import { OrderTypeChoice } from './ecommerce/common/screens/order-type-choice/OrderTypeChoice'
import {
    getCustomApiURL,
    hasChangedAPIURL
} from 'app-v2/modules/devtool/apiURL'
import { storageHttpClient, ws } from 'shared/store/ws'
import { DeveloperPage } from '../app-v2/modules/devtool/Developer'

const CART_EXPIRED = 86400000 // in ms, equals to 24 hours

const queryClient = new QueryClient({})

type Props = {
    fetchAppData: () => void,
    fetchAppSettings: () => void,
    token?: string,
    authenticated?: boolean,
    location: Object,
    intl: IntlShape,
    fetchAvailableFeatures: () => void,
    lastTimeCartModified: number,
    resetCart: () => void,
    removeProductFromCart: (product: PurchasableItem) => void,
    products: Array<PurchasableItem>,
    promotions: Array<ClickAndCollectPromotion>,
    isAuthTokenExpired: () => void,
    getAnalyticsData: () => void,
    orders: Array<any>,
    consentCookies: CookiesConsents => void
}

ws.defaults.baseURL =
    getCustomApiURL({ isDelivery: false }) || ws.defaults.baseURL

storageHttpClient.defaults.baseURL =
    getCustomApiURL({ isStorage: true }) || storageHttpClient.defaults.baseURL

class AppComponent extends Component<Props> {
    async componentDidMount() {
        const {
            fetchAppData,
            fetchAppSettings,
            authenticated,
            token,
            fetchAvailableFeatures,
            isAuthTokenExpired,
            getAnalyticsData,
            orders,
            fetchOrder
        } = this.props
        await isAuthTokenExpired()
        fetchAppData()
        fetchAppSettings()
        /** We create the setup of websocket when we have loaded page data **/
        setupWebsocket({ handleMessage: this.handleMessage, token })
        if (authenticated && !!token) {
            getWebsocket()
            getAnalyticsData()
        }
        loadReCaptcha(process.env.REACT_APP_SITE_KEY || recaptchaSiteKeyV3)
        fetchAvailableFeatures()

        if (!authenticated && orders.length > 0) {
            orders.forEach(order => {
                fetchOrder(order.id, order.token)
            })
        }

        // Check at app launch and every 3 hours that the 24 hours have not elapsed before emptying the basket
        this.handleCartExpired()
        setInterval(() => this.handleCartExpired(), 10800000)
    }

    componentWillUnmount() {
        if (process.env.REACT_APP_ENABLE_ONETRUST === 'true') {
            const cookiesConsent = {
                media: true,
                technical: true, //C0001
                tracking: true
            }
            this.props.consentCookies(cookiesConsent)
        }
    }

    handleCartExpired = () => {
        const {
            lastTimeCartModified,
            products,
            promotions,
            resetCart,
            removeProductFromCart
        } = this.props
        const hasCart = products?.length > 0 || promotions?.length > 0
        const actualTime = new Date().getTime()
        if (hasCart && lastTimeCartModified) {
            const cartTime = actualTime - lastTimeCartModified
            if (cartTime > CART_EXPIRED) {
                products.forEach(product => {
                    removeProductFromCart(product)
                })
                resetCart()
            }
        }
    }

    handleMessage = (message: any) => {
        const { type, data } = message
        /** If new websocket message */
        if (type === 'message' && data) {
            const messageJSON = JSON.parse(data)
            const { type } = messageJSON
            switch (type) {
                default:
                    break
            }
        }
    }

    render() {
        const { intl, token, authenticated, location } = this.props
        const oneTrustAvailable =
            process.env.REACT_APP_ENABLE_ONETRUST === 'true'

        return (
            <QueryClientProvider client={queryClient}>
                <GoogleOAuthProvider
                    clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                    <Meta />
                    <ConnectedRouter history={history}>
                        <ScrollToTop>
                            <Switch>
                                <Route exact path="/" component={Home} />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.signin.signup'
                                    })}
                                    component={EcommerceSigninSignupRoute}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.restaurant.choice'
                                    })}
                                    component={EcommerceRestaurantChoice}
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.delivery.choice'
                                    })}
                                    component={EcommerceDeliveryChoice}
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.clickandcollect.catalog'
                                    })}
                                    component={EcommerceCatalog}
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.clickandcollect.product'
                                    })}
                                    component={EcommerceProduct}
                                    needRestaurant
                                    needCollectMode
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.clickandcollect.menu'
                                    })}
                                    component={EcommerceMenu}
                                    needRestaurant
                                    needCollectMode
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.clickandcollect.promotions'
                                    })}
                                    component={EcommerceDiscounts}
                                    needAuthentication
                                    needRestaurant
                                    needCollectMode
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.cart.summary'
                                    })}
                                    component={AdyenOneClickPaymentWrapper}
                                    needRestaurant
                                    needCollectMode
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.cart.user.infos'
                                    })}
                                    component={EcommercePaymentWrapper}
                                    needRestaurant
                                    needCollectMode
                                    needAuthentication={false}
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.kingtable.no-desktop'
                                    })}
                                    component={KingTableNoDesktopScreen}
                                    needAuthentication={false}
                                />
                                <EcommerceRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.kingtable.scan.full'
                                    })}
                                    component={KingTableScanScreen}
                                    needAuthentication={false}
                                />
                                <EcommerceRoute
                                    path={intl.formatMessage({
                                        id: 'routes.kingtable.full'
                                    })}
                                    component={KingTableWelcomeScreen}
                                    needAuthentication={false}
                                />

                                <Route
                                    path={intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.cart.confirmation'
                                    })}
                                    component={EcommerceCartConfirmation}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.restaurants'
                                    })}
                                    component={StoreLocator}
                                />

                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.timeline'
                                    })}
                                    component={Timeline}
                                    feature={featureCodes.COMPANY_HISTORY}
                                />

                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.catalog'
                                    })}
                                    component={Catalog}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.category'
                                    })}
                                    component={Catalog}
                                />
                                <Route
                                    path={intl.formatMessage({
                                        id: 'routes.menus'
                                    })}
                                    component={Catalog}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.product.with.id'
                                    })}
                                    component={Product}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.menu.with.id'
                                    })}
                                    component={Product}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.faq'
                                    })}
                                    component={Faq}
                                />

                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.family'
                                    })}
                                    component={Family}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.customFull'
                                    })}
                                    component={Custom}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.signup'
                                    })}
                                    component={Signup}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.signin'
                                    })}
                                    component={Signin}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.mfa.confirm.code'
                                    })}
                                    component={MFAConfirmCode}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.reset.password'
                                    })}
                                    component={ResetPassword}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.congratulations'
                                    })}
                                    component={Congratulations}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.delivery'
                                    })}
                                    component={DeliveryOptions}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.restaurant.path'
                                    })}
                                    component={Restaurant}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.birthday'
                                    })}
                                    component={Birthday}
                                    feature={featureCodes.KING_BIRTHDAY}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.king.birthday'
                                    })}
                                    component={KingsBirthday}
                                    feature={featureCodes.KING_BIRTHDAY}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.satisfaction'
                                    })}
                                    component={Satisfaction}
                                />
                                <Route
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.order.type.choice'
                                    })}
                                    component={OrderTypeChoice}
                                />
                                <ProtectedRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.kingdom.dashboard'
                                    })}
                                    location={location}
                                    isAllowed={authenticated !== undefined}
                                    component={Kingdom}
                                />
                                <ProtectedRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.kingdom'
                                    })}
                                    isAllowed={token}
                                    location={location}
                                    component={Kingdom}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.cookies.management'
                                    })}
                                    component={
                                        oneTrustAvailable
                                            ? OneTrustCookieManagementScreen
                                            : CookiesManagementScreen
                                    }
                                    feature={
                                        featureCodes.ENABLE_COOKIES_CONSENT
                                    }
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking'
                                    })}
                                    component={TuPreferesKingHome}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking.duel.win'
                                    })}
                                    component={TuPreferesKingDuelWin}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking.duel'
                                    })}
                                    component={TuPreferesKingDuel}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking.goodiesGain'
                                    })}
                                    component={TuPreferesKingGoodiesGain}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking.qrCode'
                                    })}
                                    component={TuPreferesKingQrCode}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.tupreferesking.email'
                                    })}
                                    component={TuPreferesKingEmailForm}
                                    feature={featureCodes.TU_PREFERES_KING}
                                />
                                <FeatureToggledRoute
                                    exact
                                    path={intl.formatMessage({
                                        id: 'routes.complaint'
                                    })}
                                    component={Complaint}
                                    feature={
                                        featureCodes.FRANCHISED_RECLAMATION
                                    }
                                />
                                {hasChangedAPIURL() ? (
                                    <Route
                                        exact
                                        path={intl.formatMessage({
                                            id: 'routes.dev'
                                        })}
                                        component={DeveloperPage}
                                    />
                                ) : (
                                    <FeatureToggledRoute
                                        exact
                                        path={intl.formatMessage({
                                            id: 'routes.dev'
                                        })}
                                        component={DeveloperPage}
                                        feature={featureCodes.DEV}
                                    />
                                )}
                                <Route component={NotFound} />
                            </Switch>
                            {token && <Toast />}
                            <NotificationsPermission />
                        </ScrollToTop>
                    </ConnectedRouter>
                </GoogleOAuthProvider>
            </QueryClientProvider>
        )
    }
}

const mapStateToProps = (state: RootState) => {
    const {
        lastTimeCartModified,
        products,
        promotions,
        cacOrders
    } = state.ecommerceOrderState

    const { cookiesConsentsDone } = state.userState

    const sortedOrders = (Object.values(cacOrders): any).sort(
        (o1: Order, o2: Order) => {
            return moment(o2.creationDate).diff(moment(o1.creationDate))
        }
    )
    return {
        ...state.userState,
        lastTimeCartModified,
        products,
        promotions,
        orders: sortedOrders,
        cookiesConsentsDone
    }
}

const mapDispatchToProps = (d: Dispatch<any>) => ({
    fetchAppData: () => d(fetchApplicationData()),
    fetchAppSettings: () => d(fetchAppSettings()),
    fetchOrder: (id, token) => d(fetchOrder(id, token)),
    fetchAvailableFeatures: () => d(fetchAvailableFeatures()),
    resetCart: () => d(resetCart()),
    removeProductFromCart: (product: PurchasableItem) =>
        d(removeProductFromCart(product)),
    isAuthTokenExpired: () => d(isAuthTokenExpired()),
    getAnalyticsData: () => d(getAnalyticsData()),
    consentCookies: (consents: CookiesConsents) => d(consentCookies(consents))
})

const App = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(AppComponent))

export { App }
